import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { UserPlus, Copy, Trash2, Pencil, Link2, Users, Search, X, Lock, Unlock } from "lucide-react";

export default function FuncionariosPage() {
  const { authAxios } = useAuth();
  const [funcionarios, setFuncionarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [nome, setNome] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchFuncionarios = useCallback(async () => {
    try {
      const res = await authAxios.get("/hub-empresa/funcionarios");
      setFuncionarios(res.data);
    } catch {
      toast.error("Erro ao carregar funcionarios");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { fetchFuncionarios(); }, [fetchFuncionarios]);

  const openCreate = () => {
    setEditing(null);
    setNome("");
    setDialogOpen(true);
  };

  const openEdit = (func) => {
    setEditing(func);
    setNome(func.nome);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!nome.trim()) { toast.error("Informe o nome"); return; }
    setSaving(true);
    try {
      if (editing) {
        await authAxios.put(`/hub-empresa/funcionarios/${editing.id}`, { nome: nome.trim() });
        toast.success("Funcionario atualizado!");
      } else {
        await authAxios.post("/hub-empresa/funcionarios", { nome: nome.trim() });
        toast.success("Funcionario criado! Link publico gerado automaticamente.");
      }
      setDialogOpen(false);
      fetchFuncionarios();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const toggleAtivo = async (func) => {
    try {
      await authAxios.put(`/hub-empresa/funcionarios/${func.id}`, { ativo: !func.ativo });
      toast.success(func.ativo ? "Conta bloqueada" : "Conta desbloqueada");
      fetchFuncionarios();
    } catch {
      toast.error("Erro ao atualizar status");
    }
  };

  const handleDelete = async (func) => {
    if (!window.confirm(`Excluir funcionario "${func.nome}"? Esta acao nao pode ser desfeita.`)) return;
    try {
      await authAxios.delete(`/hub-empresa/funcionarios/${func.id}`);
      toast.success("Funcionario excluido");
      fetchFuncionarios();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const copyLink = (func) => {
    const url = `${window.location.origin}/funcionario/${func.token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const filtered = funcionarios.filter(f =>
    f.nome.toLowerCase().includes(search.toLowerCase())
  );

  const totalAtivos = funcionarios.filter(f => f.ativo).length;
  const totalBloqueados = funcionarios.filter(f => !f.ativo).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Funcionarios</h1>
          <p className="text-muted-foreground text-sm">
            Gerencie acessos publicos para Hub Empresa, Revenda, Acomodado e Rev. Acomodado
          </p>
        </div>
        <Button onClick={openCreate} data-testid="funcionarios-novo-btn">
          <UserPlus className="w-4 h-4 mr-2" /> Novo Funcionario
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-primary/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Users className="w-5 h-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{funcionarios.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10"><Unlock className="w-5 h-5 text-emerald-500" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Ativos</p>
              <p className="text-2xl font-bold text-emerald-500">{totalAtivos}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-red-500/30">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10"><Lock className="w-5 h-5 text-red-500" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Bloqueados</p>
              <p className="text-2xl font-bold text-red-500">{totalBloqueados}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Busca */}
      <div className="relative max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          className="pl-9 pr-9"
          placeholder="Buscar por nome..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="funcionarios-search-input"
        />
        {search && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-red-500 hover:text-red-600"
            onClick={() => setSearch("")}
            data-testid="funcionarios-search-clear"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Lista */}
      {loading ? (
        <p className="text-center py-12 text-muted-foreground">Carregando...</p>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-40" />
            <p>Nenhum funcionario cadastrado</p>
            <Button onClick={openCreate} className="mt-4" variant="outline">
              <UserPlus className="w-4 h-4 mr-2" /> Cadastrar primeiro funcionario
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(func => (
            <Card key={func.id} className={func.ativo ? "border-primary/30" : "border-red-500/30 opacity-75"} data-testid={`funcionario-card-${func.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg ${func.ativo ? "bg-primary/10" : "bg-red-500/10"}`}>
                      <Users className={`w-5 h-5 ${func.ativo ? "text-primary" : "text-red-500"}`} />
                    </div>
                    <div className="min-w-0">
                      <CardTitle className="text-lg truncate">{func.nome}</CardTitle>
                      <Badge variant={func.ativo ? "default" : "destructive"} className="mt-1 text-xs">
                        {func.ativo ? "Ativo" : "Bloqueado"}
                      </Badge>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-2 space-y-3">
                <div className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                  <div className="flex items-center gap-2 text-sm">
                    {func.ativo ? <Unlock className="w-4 h-4 text-emerald-500" /> : <Lock className="w-4 h-4 text-red-500" />}
                    <span className="text-muted-foreground">
                      {func.ativo ? "Acesso liberado" : "Acesso bloqueado"}
                    </span>
                  </div>
                  <Switch
                    checked={func.ativo}
                    onCheckedChange={() => toggleAtivo(func)}
                    data-testid={`funcionario-toggle-${func.id}`}
                  />
                </div>

                <div className="flex items-center gap-2 p-2 rounded-md bg-muted/30 text-xs font-mono break-all">
                  <Link2 className="w-3.5 h-3.5 shrink-0 text-primary" />
                  <span className="truncate">/funcionario/{func.token}</span>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => copyLink(func)}
                    data-testid={`funcionario-copy-${func.id}`}
                  >
                    <Copy className="w-3.5 h-3.5 mr-1" /> Link
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9"
                    onClick={() => openEdit(func)}
                    data-testid={`funcionario-edit-${func.id}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 text-destructive hover:text-destructive"
                    onClick={() => handleDelete(func)}
                    data-testid={`funcionario-delete-${func.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Funcionario" : "Novo Funcionario"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Atualize o nome do funcionario."
                : "Ao criar, um link publico unico sera gerado automaticamente. Esse link da acesso ao Hub Empresa, Revenda, Acomodado e Rev. Acomodado."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="func-nome">Nome do Funcionario</Label>
            <Input
              id="func-nome"
              placeholder="Ex: Carlos Silva"
              value={nome}
              onChange={e => setNome(e.target.value)}
              data-testid="funcionario-nome-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="funcionario-save-btn">
              {saving ? "Salvando..." : (editing ? "Salvar" : "Criar Funcionario")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
